import { useContext, useMemo } from "react";
import { EntityByEntityTypeId, GenericEntityConstantsEntities } from "../../api/GenericConstants";
import { SessionContext } from "../contexts/SessionContext";
import { IEntityPermissions } from "../../api/GenericTypes";

export interface IGenericEntityPermissions<EntityTypeId extends GenericEntityConstantsEntities = any> {
  canCreate: boolean;
  canRead: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canReadIndex: boolean;
  canEdit: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canDelete: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canRestore: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canTrash: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canBulkEdit: (bulkPermissions: IEntityPermissions | undefined) => boolean;
  canBulkDelete: (bulkPermissions: IEntityPermissions | undefined) => boolean;
  canBulkRestore: (bulkPermissions: IEntityPermissions | undefined) => boolean;
  canLock: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canBulkLock: (bulkPermissions: IEntityPermissions | undefined) => boolean;
  canUnlock: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canBulkUnlock?: (bulkPermissions: IEntityPermissions | undefined) => boolean;
  canSign: (entity: EntityByEntityTypeId<EntityTypeId>) => boolean;
  canBulkSign?: (bulkPermissions: IEntityPermissions | undefined) => boolean;
}

const defaultPermissions: IGenericEntityPermissions = {
  canCreate: false,
  canRead: (entity: any) => false,
  canReadIndex: false,
  canEdit: (entity: any) => false,
  canDelete: (entity: any) => false,
  canRestore: (entity: any) => false,
  canTrash: (entity: any) => false,
  canBulkEdit: (bulkPermissions: any) => false,
  canBulkDelete: (bulkPermissions: any) => false,
  canBulkRestore: (bulkPermissions: any) => false,
  canLock: (entity: any) => false,
  canBulkLock: (bulkPermissions: any) => false,
  canUnlock: (entity: any) => false,
  canBulkUnlock: (bulkPermissions: any) => false,
  canSign: (entity: any) => false,
  canBulkSign: (bulkPermissions: any) => false,
} as const;

/**
 * Custom hook to retrieve permissions for various entities based on the current session.
 * @author @CorradoSurmanowicz
 * @returns {Object} An object containing permissions for different entities.
 *
 * @example
 * const { permissions } = usePermissions();
 *
 * // Check if the user can create announcements
 * const canCreateAnnouncements = permissions.announcements.canCreate;
 *
 * // Check if the user can read a specific attachment
 * const canReadAttachment = permissions.attachments.canRead(someAttachment);
 *
 * @typedef {Object} IGenericEntityPermissions
 * @property {boolean} canCreate - Indicates if the user can create the entity.
 * @property {function} canRead - Function to check if the user can read a specific entity.
 * @property {boolean} canReadIndex - Indicates if the user can read the index of the entity.
 * @property {function} canEdit - Function to check if the user can edit a specific entity.
 * @property {function} canBulkEdit - Function to check if the user can bulk edit entities.
 * @property {function} canDelete - Function to check if the user can delete a specific entity.
 * @property {function} canBulkDelete - Function to check if the user can bulk delete entities.
 * @property {function} canRestore - Function to check if the user can restore a specific entity.
 * @property {function} canBulkRestore - Function to check if the user can bulk restore entities.
 * @property {function} canTrash - Function to check if the user can trash a specific entity.
 * @property {function} canLock - Function to check if the user can lock a specific entity.
 * @property {function} canBulkLock - Function to check if the user can bulk lock entities.
 * @property {function} canUnlock - Function to check if the user can unlock a specific entity.
 * @property {function} canBulkUnlock - Function to check if the user can bulk unlock entities.
 * @property {function} canSign - Function to check if the user can sign a specific entity.
 * @property {function} canBulkSign - Function to check if the user can bulk sign entities.
 */

export const usePermissions = () => {
  const { session } = useContext(SessionContext);
  const permissions = useMemo(
    () => ({
      announcements: {
        canCreate: !!session?.permissions?.administer_announcements,
        canRead: (entity) => !!session?.permissions?.view_announcements,
        canReadIndex: !!session?.permissions?.view_announcements,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"announcements">,
      apiKeys: {
        canCreate: true,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => true,
        canBulkEdit: (bulkPermissions) => true,
        canDelete: (entity) => true,
        canBulkDelete: (bulkPermissions) => true,
        canRestore: (entity) => true,
        canBulkRestore: (bulkPermissions) => true,
        canTrash: (entity) => true,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"apiKeys">,
      attachments: {
        canCreate: !!session?.permissions?.add_datasets,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex:
          !!session?.permissions?.logsadmin ||
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.view_all_attachments,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"attachments">,
      auditLogs: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex:
          !!session?.permissions?.logsadmin ||
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.view_global_audit_log,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"auditLogs">,
      bridges: {
        canCreate: !!session?.permissions?.administer_autoload,
        canRead: (entity) => !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canReadIndex: !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"bridges">,
      customFields: {
        canCreate: !!session?.permissions?.administer_custom_field_schemas,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customFields">,
      customImports: {
        canCreate: false,
        canRead: (entity) =>
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.apply_custom_imports ||
          !!session?.permissions.administer_autoload,
        canReadIndex:
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.apply_custom_imports ||
          !!session?.permissions.administer_autoload,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customImports">,
      customImportLogs: {
        canCreate: false,
        canRead: (entity) =>
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.apply_custom_imports ||
          !!session?.permissions.administer_autoload,
        canReadIndex:
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.apply_custom_imports ||
          !!session?.permissions.administer_autoload,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) =>
          !!session?.permissions?.view_admin_pages ||
          !!session?.permissions?.apply_custom_imports ||
          !!session?.permissions.administer_autoload,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customImports">,
      customTypes: {
        canCreate: !!session?.permissions?.administer_custom_field_schemas,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customTypes">,
      customTypeTemplates: {
        canCreate: false,
        canRead: (entity) =>
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canReadIndex:
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customTypeTemplates">,
      customFieldTemplates: {
        canCreate: false,
        canRead: (entity) =>
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canReadIndex:
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customTypeTemplates">,
      customTypeTemplatePackages: {
        canCreate: false,
        canRead: (entity) =>
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canReadIndex:
          !!session?.permissions?.view_admin_pages || !!session?.permissions.administer_custom_field_schemas,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"customTypeTemplatePackages">,
      datasets: {
        canCreate: !!session?.permissions?.add_datasets,
        canRead: (entity) => true, // to allow reading in shared content
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"datasets">,
      dataFormats: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"dataFormats">,
      dataSources: {
        canCreate: !!session?.permissions?.administer_autoload,
        canRead: (entity) => !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canReadIndex: !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"dataSources">,
      dataSourceStatus: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canReadIndex: !!session?.permissions?.view_admin_pages || !!session?.permissions?.administer_autoload,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"dataSourceStatus">,
      fileExportJobs: {
        canCreate: false,
        canRead: (entity) => true,
        canReadIndex: true,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => bulkPermissions?.canDelete ?? false,
        canDelete: (entity) => entity.permissions?.canDelete ?? false,
        canBulkDelete: (bulkPermissions) => true,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"fileExportJobs">,
      iconNMRHistoryEntry: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"iconNMRHistoryEntry">,
      iconNMRHolders: {
        canCreate: !!session?.permissions?.administer_autoload,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"iconNMRHolders">,
      inventories: {
        canCreate: !!session?.permissions?.administer_custom_field_schemas,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"inventories">,
      inventoryItems: {
        canCreate:
          !!session?.permissions?.add_inventory_items ||
          !!session?.permissions.add_admin_inventory_items ||
          !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"inventoryItems">,
      notebooks: {
        canCreate: !!session?.permissions?.can_create_eln_content || !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"notebooks">,
      notebookExperiments: {
        canCreate: !!session?.permissions?.can_create_eln_content || !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"notebookExperiments">,
      notebookEntries: {
        canCreate: !!session?.permissions?.can_create_eln_content || !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"notebookEntries">,
      notebookTemplates: {
        canCreate: !!session?.permissions?.can_create_eln_content || !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.features?.eln && !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"notebookTemplates">,
      methods: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"methods">,
      persons: {
        canCreate: !!session?.permissions?.administer_persons,
        canRead: (entity) => session?.permissions?.view_all_persons || session?.userId === entity.id,
        canReadIndex: !!session?.permissions?.view_all_persons,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"persons">,
      projects: {
        canCreate: !!session?.permissions?.administer_projects || !!session?.permissions?.add_projects,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"projects">,
      roles: {
        canCreate: !!session?.permissions?.logsadmin,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"roles">,
      samples: {
        canCreate: !!session?.permissions.add_samples,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => !!entity?.isDeleted && !!entity?.permissions?.canRestore,
        canBulkRestore: (bulkPermissions) => !!bulkPermissions?.canRestore,
        canTrash: (entity) => !entity?.isDeleted && !!entity?.permissions?.canTrash,
        canLock: (entity) => !entity?.isLocked && !!entity?.permissions?.canLock,
        canBulkLock: (bulkPermissions) => !!bulkPermissions?.canLock,
        canUnlock: (entity) => !!entity?.isLocked && !!entity?.permissions?.canUnlock,
        canBulkUnlock: (bulkPermissions) => !!bulkPermissions?.canUnlock,
        canSign: (entity) => !entity?.isSigned && !!entity?.permissions?.canSign,
        canBulkSign: (bulkPermissions) => !!bulkPermissions?.canSign,
      } as IGenericEntityPermissions<"samples">,
      sharedContents: {
        canCreate: !!session?.permissions?.add_bundles,
        canRead: (entity) => true, // Shared content checks for owner based permissions
        canReadIndex: true,
        canEdit: (entity) => !!entity?.permissions?.canEdit,
        canBulkEdit: (bulkPermissions) => !!bulkPermissions?.canEdit,
        canDelete: (entity) => !!entity?.permissions?.canDelete,
        canBulkDelete: (bulkPermissions) => !!bulkPermissions?.canDelete,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"sharedContents">,
      vendors: {
        canCreate: false,
        canRead: (entity) => !!session?.permissions?.view_basic_content,
        canReadIndex: !!session?.permissions?.view_basic_content,
        canEdit: (entity) => false,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) => false,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"vendors">,
      zontalCredentials: {
        canCreate: !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canRead: (entity) => !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canReadIndex: !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canEdit: (entity) => !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) =>
          !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"zontalCredentials">,
      zontalIPP: {
        canCreate: !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canRead: (entity) => !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canReadIndex: !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canEdit: (entity) => !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canBulkEdit: (bulkPermissions) => false,
        canDelete: (entity) =>
          !!session?.permissions?.view_admin_pages && !!session?.features?.enable_zontal_integration,
        canBulkDelete: (bulkPermissions) => false,
        canRestore: (entity) => false,
        canBulkRestore: (bulkPermissions) => false,
        canTrash: (entity) => false,
        canLock: (entity) => false,
        canBulkLock: (bulkPermissions) => false,
        canUnlock: (entity) => false,
        canBulkUnlock: (bulkPermissions) => false,
        canSign: (entity) => false,
        canBulkSign: (bulkPermissions) => false,
      } as IGenericEntityPermissions<"zontalIPP">,
    }),
    [session]
  );
  return { permissions };
};

interface UseEntityPermissionsProps {
  entityTypeId: GenericEntityConstantsEntities;
}
/**
 * Hook to retrieve permissions for a specific entity type.
 * @author @CorradoSurmanowicz
 * @template EntityTypeId - The type of the entity.
 * @param {UseEntityPermissionsProps} props - The properties for the hook.
 * @param {GenericEntityConstantsEntities} props.entityTypeId - The ID of the entity type.
 * @returns {IGenericEntityPermissions<EntityTypeId>} The permissions for the specified entity type.
 *
 * @example
 * ```typescript
 * const permissions = useEntityPermissions({ entityTypeId: "announcements" });
 * if (permissions.canCreate) {
 *   // User can create announcements
 * }
 * ```
 */

export const useEntityPermissions = <EntityTypeId extends GenericEntityConstantsEntities>({
  entityTypeId,
}: UseEntityPermissionsProps): IGenericEntityPermissions<EntityTypeId> => {
  const { session } = useContext(SessionContext);
  const { permissions } = usePermissions();
  if (!session) return defaultPermissions;
  if (!Object.hasOwn(permissions, entityTypeId)) return defaultPermissions;
  return permissions[entityTypeId] as IGenericEntityPermissions<EntityTypeId>;
};
